<script>
import MixedIcons from '../../../assets/icons/MixedIcons.vue';
import { mapActions } from 'vuex';
import SustSelectField from '../../../components/SustSelectField.vue';

export default {
    components: {
        MixedIcons,
        SustSelectField,
    },

    data() {
        return {
            logTypes: [],

            relatedCompanies: [],
            selectedRelatedCompany: null,

            logs: [],
            pageCount: 1, 

            selectedTab: "my_company",
            selectedPage: 1,

            loading: false,
        }
    },

    mounted() {
        this._getRelatedCompanies();
        this._getLogs();
    },

    methods: {
        ...mapActions('company', [
            'getRelatedCompanies',
        ]),
        ...mapActions('admin', [
            'getLogs'
        ]),
        ...mapActions('KPIsMetrics', [
            'redirectToMetric'
        ]),

        _getRelatedCompanies() {
            this.getRelatedCompanies()
                .then(res => {
                    this.relatedCompanies = res.data.value;
                })
                .catch(() => {
                    console.error('Error fetching related companies!');
                })
                .finally(() => {
                    if (this.relatedCompanies.length > 0) 
                        this.selectedRelatedCompany = this.relatedCompanies[0].company_id;
                })
        },
        
        _getLogs() {
            this.loading = true;

            const params = {
                company_id: this.selectedTab === 'my_company' ? 'none' : this.selectedRelatedCompany,
                page: this.selectedPage
            };

            this.getLogs(params)
                .then( res => {
                    this.logs = res.data.logs;
                    this.pageCount = res.data.total_pages;
                })
                .catch(error => {
                    console.error(error.message);
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        _redirectToMetric(log) {
            if (log.special_case === 'metric') {
                const params = {
                    metric_kpi_id: log.metric_id || null,
                    sub_metric_kpi_id: log.sub_metric_id || null,
                };

                this.redirectToMetric(params)
                    .then(res => {
                        window.open(res.data.redirect, '_blank');
                    })
                    .catch(res => {
                        window.open(res.data.redirect, '_blank');
                    })
            }
        },

        // event handlers //

        selectTab(tab) {
            try {
                this.selectedTab = tab;
                this.resetAll();
            } catch (error) {
                console.error(error.message);
            } finally {
                this._getLogs();
            }
        },

        resetAll() {
            this.logs = [];
            this.pageCount = 1;
            this.selectedPage = 1;
        },

        handlePageChange(direction) {
            if (direction === 'left' && this.selectedPage > 1) {
                this.selectedPage--;
            } else if (direction === 'right' && this.selectedPage < this.pageCount) {
                this.selectedPage++;
            } else {
                return;
            }
            
            this._getLogs();
        },

        handleRelatedCompanyChange() {
            try {
                this.resetAll();
            } catch (error) {
                console.error(error.message);
            } finally {
                this._getLogs();
            }
        },

        logExpandController(item) {
            if (this.isNonExpandable(item)) return;

            item.expanded = !item.expanded;
        },

        isNonExpandable(item) {
            return item.action_type === 'login' || item.action_type === 'logout' || item.action_type === 'report_graph';
        }
    }
}
</script>

<template>
    <div class="flex-all-centered-col">
        <div v-if="relatedCompanies.length > 0" class="logs-selector">
            <div :class="{'selected': selectedTab == 'my_company', 'inactive': selectedTab != 'my_company'}"
                @click="selectTab('my_company')">
                {{ $t('logs.me') }}
            </div>
            <div :class="{'selected': selectedTab == 'related_companies', 'inactive': selectedTab != 'related_companies'}"
                @click="selectTab('related_companies')">
                {{ $t('logs.all') }}
            </div>
        </div>

        <div v-if="loading" class="flex-all-centered-row" style="padding: 1em; font-size: 2.5em;">
            <loading-spinner />
        </div>

        <div v-else style="width: 100%; height: 90%;">
            <div class="buttons-container">
                <div class="refresh-button" @click="handleRelatedCompanyChange()">
                    <i class="fa-solid fa-arrows-rotate"></i>
                </div>

                <sust-select-field v-if="selectedTab === 'related_companies' && relatedCompanies.length > 0"
                    style="max-width: 15%; z-index: 9999;" v-model="selectedRelatedCompany" :options="relatedCompanies"
                    identifier_key="company_id" identifier_name="company_name" @input="handleRelatedCompanyChange()" />
            </div>

            <div class="buttons-container">
                <div class="flex-all-centered-row" style="justify-content: flex-start;">
                    <button class="sust-btn sust-btn-navy" @click="handlePageChange('left')" style="margin-right: 1em;"
                        :disabled="selectedPage <= 1">
                        Previous
                    </button>

                    <button class="sust-btn sust-btn-navy" @click="handlePageChange('right')" style="margin-right: 1em;"
                        :disabled="selectedPage >= pageCount">
                        Next
                    </button>

                    <div>
                        Current Page: <b>{{ selectedPage }} / {{ pageCount }}</b>
                    </div>
                </div>
            </div>

            <br>
            <div class="logs-container">
                <div v-for="(item, index) in logs" :key="index" class="log-item-upper-wrapper"
                    :class="{'non-expandable': isNonExpandable(item)}" @click="logExpandController(item)">
                    <div class="log-item">
                        <div class="log-icon">
                            <mixed-icons :name="item.special_case" :side_class="item.action_type" :hover="false" />
                        </div>
                        <div class="log-details">
                            <div class="log-message">
                                {{ item.message }} - <span>{{ $t('logs.by_sentence').replace('#s#', item.actor_name) }}</span>
                            </div>
                            <div class="log-date">
                                {{ item.action_date | dateFormat }}
                            </div>
                            <div class="expanded-data" v-show="item.expanded">
                                <div>
                                    <span>{{ $t('logs.details.acted_entity_name') }}:</span> {{ item.acted_entity_name }}
                                </div>

                                <div v-if="item.acted_entity_parent_name">
                                    <span>{{ $t('logs.details.acted_entity_parent_name') }}:</span> {{ item.acted_entity_parent_name }}
                                </div>
                                
                                <div v-if="item.report_type">
                                    <span>{{ $t('logs.details.report_type') }}:</span> {{ item.report_type }}
                                </div>

                                <div v-if="item.acted_upon">
                                    <span>{{ $t('logs.details.acted_upon') }}:</span> {{ item.acted_upon }}
                                </div>

                                <div v-if="item.new_value !== null && !item.is_date">
                                    <span>{{ $t('logs.details.change') }}:</span>
                                    <span style="color: var(--invalid-input) !important;"> {{ item.old_value || 'N/A' }} </span> 
                                    <span style="color: white !important;"> &rarr; </span>
                                    <span style="color: var(--sustainfinity-green) !important;"> {{ item.new_value }}</span>
                                </div>
                                <div v-else-if="item.new_value !== null && item.is_date">
                                    <span>{{ $t('logs.details.change') }}:</span>
                                    <span style="color: var(--invalid-input) !important;"> {{ item.old_value | dateFormat }} </span> 
                                    <span style="color: white !important;"> &rarr; </span>
                                    <span style="color: var(--sustainfinity-green) !important;"> {{ item.new_value | dateFormat }}</span>
                                </div>

                                <div v-if="item.year && item.quarter">
                                    <span>{{ $t('logs.details.year_data') }}:</span> {{ item.year }} - {{ item.quarter }}
                                </div>

                                <button v-if="item.special_case === 'metric'"
                                    class="sust-btn redirect-metric-button"
                                    @click="_redirectToMetric(item)"
                                >
                                    <i class="fa-solid fa-square-arrow-up-right"></i> 
                                    {{ $t('misc.take_me_there') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="logs.length == 0" class="nothing-yet">
                    {{ $t('logs.nothing') }}
                </div>
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .logs-container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: flex-start;

        width: 100%;
        height: 95% !important;

        margin-bottom: 1em !important;

        > .log-item-upper-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-content: flex-start;

            background-color: var(--sustainfinity-navy);
            border-radius: .5em;
            box-shadow: 0 0 .5em .1em gray;    
            margin: .5em 0 .5em 0;
            padding: 1em 0 1em 1em;

            &:hover {
                cursor: pointer;
                background-color: var(--sustainfinity-navy--hover);
            }

            > .log-item {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: flex-start;
    
                > .log-icon {
                    display: flex;
                    justify-content: center;
                    align-content: center;
                    align-items: center;
    
                    width: 10%;
                    margin-right: 1em;
                }
    
                > .log-details {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-content: flex-start;
                    align-items: flex-start;
                    text-align: left;
    
                    width: 100%;
                    
                    > .log-message {
                        color: white;
                        font-weight: bold;
                    }
    
                    > .log-date {
                        color: var(--icon-main-transparent);
                        font-size: .9em;
                        font-weight: bold;
                    }

                    > .expanded-data {
                        display: flex;
                        flex-direction: column;
                        justify-content: flex-start;
                        align-items: flex-start;
                        padding-top: .5em;

                        > div > span {
                            color: var(--sustainfinity-yellow) !important;
                            font-weight: bold;
                            font-size: 1.05em;
                        }

                        > div {
                            color: rgb(215, 215, 215);
                        }
                    }
                }
            }
        }

        > .non-expandable {
            pointer-events: none !important;
            cursor: default !important;
            background-color: var(--sustainfinity-navy) !important;
            &:hover {
                background-color: var(--sustainfinity-navy) !important;
                cursor: default !important;
            }
        }
    }

    .logs-selector {
        margin-top: 1em !important;

        display: flex;
        flex-direction: row;
        justify-content: center;
        align-content: center;
        align-items: center;
        margin: auto;

        border-radius: 1.5em;
        border: thin solid var(--sustainfinity-navy);
        box-shadow: 0 0 .5em .1em gray;

        max-width: fit-content;
        overflow: hidden;

        > div {
            padding: .8em;
            cursor: pointer;
            transition: background-color 250ms;

            &:first-of-type {
                border-right: thin solid var(--sustainfinity-navy);
            }
        }

        > .inactive {
            background-color: whitesmoke !important;
            color: var(--sustainfinity-navy) !important;
            &:hover {
                background-color: lightgray !important;
            }
        }

        > .selected {
            background-color: var(--sustainfinity-navy) !important;
            color: whitesmoke !important;
        }
    }

    .nothing-yet {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-content: center;
        align-items: center;
        width: 100%;

        font-size: 2em;
        font-weight: bold;
    }

    .buttons-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;

        > .refresh-button {
            margin-bottom: 1em;
            max-width: fit-content;
            text-align: center;
    
            background-color: var(--sustainfinity-blue);
            color: white;
            padding: .5em 1em .5em 1em;
            border-radius: 1em;
            box-shadow: 0 0 1em .1em gray;
    
            transition: all 100ms;
            cursor: pointer;
            &:hover {
                background-color: var(--sustainfinity-navy);
            }
        }
    }

    .redirect-metric-button {
        box-shadow: none; 
        color: var(--sustainfinity-navy);
        background-color: var(--sustainfinity-faded-green);
        margin-top: .5em;

        &:hover {
            background-color: var(--sustainfinity-green);
            color: whitesmoke;
        }
    }
</style>
